import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFormSecondaryAction,
  AdminFormDropdownChevron,
  AdminHeaderAction,
  AdminPanelTitle,
  AdminSupportingText,
  ADMIN_DETAIL_TABLE_CLASS,
  adminFormDetailSelectClasses,
  adminFormTextareaClasses,
} from '../shared/adminUx';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import {
  getActionTypeLabel,
  getModerationDecisionButtons,
  isFraudSecurityTransferOutcome,
  normalizeCaseActionForm,
  normalizeDecisionOutcome,
  requiresConfirmationMessage,
  resolveModerationDecisionButtonClick,
} from '../../../data/caseActionsConfig';
import {
  buildFlaggedItems,
  decisionIdFromCaseId,
  deriveDecisionOutcomeFromFindings,
  FINDING_OPTIONS,
  formatQueueDateTime,
  getModerationDecisionChecklist,
} from '../../../data/moderationQueueSpec';

const DECISION_BUTTONS = getModerationDecisionButtons();
const TRANSFER_BUTTON = DECISION_BUTTONS.find(
  (rule) => normalizeDecisionOutcome(rule.decisionOutcome) === 'Transfer'
);
const CONFIRMED_BUTTON = DECISION_BUTTONS.find(
  (rule) => normalizeDecisionOutcome(rule.decisionOutcome) === 'Confirmed'
);
const NOT_CONFIRMED_BUTTON = DECISION_BUTTONS.find(
  (rule) => normalizeDecisionOutcome(rule.decisionOutcome) === 'Not Confirmed'
);

const formatAppliedActions = (actions = []) => {
  if (!actions.length) return '—';
  return actions
    .map((action) => getActionTypeLabel(action.actionType) || action.ruleName || action.actionType)
    .filter(Boolean)
    .join('; ');
};

const DetailField = ({ label, value }) => (
  <div className="space-y-1">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <AdminDetailFieldValue>
      {value != null && value !== '' ? value : '—'}
    </AdminDetailFieldValue>
  </div>
);

const CaseChecklist = ({ checks, disabled, onToggle }) => (
  <div>
    <AdminPanelTitle className="mb-4">Case Checklist</AdminPanelTitle>
    <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
      <div className="space-y-3">
        {checks.map((check) => (
          <label
            key={check.id}
            className={`flex items-start gap-2 ${
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
            }`}
          >
            <input
              type="checkbox"
              checked={Boolean(check.checked)}
              disabled={disabled}
              onChange={() => onToggle?.(check.id)}
              className="w-4 h-4 mt-0.5 text-primaryblue shrink-0"
            />
            <span className="text-offblack text-sm">{check.label}</span>
          </label>
        ))}
      </div>
    </DashboardMainContainer>
  </div>
);

const buildDecisionDetailFields = (record = {}) => {
  const fields = [
    { label: 'Moderation Decision ID', value: record.moderationDecisionId },
    { label: 'Decision Outcome', value: record.decisionOutcome },
    { label: 'Decision Action', value: record.decisionActionLabel || record.decisionActionType },
    {
      label: 'Applied Case Actions',
      value: record.appliedActionsLabel || formatAppliedActions(record.appliedActions),
    },
    { label: 'Decision Reason', value: record.decisionReason },
  ];

  if (isFraudSecurityTransferOutcome(record.decisionOutcome)) {
    fields.push({
      label: 'Fraud & Security',
      value: 'Transferred — case leaves Moderation (no Archive entry)',
    });
  }

  (record.findings || record.flaggedItems || []).forEach((item) => {
    fields.push({
      label: item.label,
      value: item.finding || record.decisionOutcome || '—',
    });
  });

  fields.push(
    { label: 'Finalised By', value: record.finalisedBy },
    { label: 'Finalised On', value: formatQueueDateTime(record.finalisedOn) }
  );

  return fields;
};

const RecordedDecisionFields = ({ record }) => (
  <div className="space-y-4">
    {buildDecisionDetailFields(record).map((field) => (
      <DetailField key={field.label} label={field.label} value={field.value} />
    ))}
  </div>
);

const ArchivedDecisionRecord = ({ record }) => {
  const fields = buildDecisionDetailFields(record);
  const mid = Math.ceil(fields.length / 2);

  return (
    <div className="space-y-8">
      <CaseChecklist checks={record.checks || []} disabled />
      <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            {fields.slice(0, mid).map((field) => (
              <DetailField key={field.label} label={field.label} value={field.value} />
            ))}
          </div>
          <div className="space-y-4">
            {fields.slice(mid).map((field) => (
              <DetailField key={field.label} label={field.label} value={field.value} />
            ))}
          </div>
        </div>
      </DashboardMainContainer>
    </div>
  );
};

const QueueDecisionForm = ({
  scenario,
  itemFromState,
  caseStatus = 'New',
  assignedHandler = 'Unassigned',
  onFinalSave,
  onProgressSave,
  onAssignToMe,
  initialDraft,
}) => {
  const isNew = caseStatus === 'New';
  const isAssigned =
    assignedHandler && assignedHandler !== 'Unassigned' && assignedHandler !== '—';
  const decisionAvailable = caseStatus !== 'Decided' && isAssigned;
  const checklist = useMemo(() => getModerationDecisionChecklist(), []);
  const [flaggedItems, setFlaggedItems] = useState(() =>
    (initialDraft?.flaggedItems || buildFlaggedItems(itemFromState || {}, scenario) || []).map(
      (item) => ({
        ...item,
        finding: item.finding || '',
      })
    )
  );
  const [checks, setChecks] = useState(() =>
    initialDraft?.checks || checklist.map((item) => ({ ...item, checked: false }))
  );
  const [decisionReason, setDecisionReason] = useState(initialDraft?.decisionReason || '');
  const [saved, setSaved] = useState(null);
  const [pendingConfirmRule, setPendingConfirmRule] = useState(null);

  const locked = Boolean(saved) || caseStatus === 'Decided';
  const missingChecks = checks.filter((item) => item.mandatory && !item.checked);
  const derivedOutcome = deriveDecisionOutcomeFromFindings(flaggedItems);
  const formReady =
    !missingChecks.length &&
    Boolean(decisionReason.trim()) &&
    Boolean(derivedOutcome) &&
    flaggedItems.length > 0;
  const actionsAvailable = decisionAvailable && !locked;

  const toggleCheck = (id) => {
    if (!decisionAvailable || locked) return;
    setChecks((prev) =>
      prev.map((item) => (item.id === id ? { ...item, checked: !item.checked } : item))
    );
  };

  const setItemFinding = (id, finding) => {
    if (!decisionAvailable || locked) return;
    setFlaggedItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, finding } : item))
    );
  };

  const buildDraft = (outcome = derivedOutcome) => ({
    checks,
    flaggedItems,
    decisionReason,
    decisionOutcome: outcome || null,
  });

  const executeFinal = (actionRule, outcome) => {
    if (!actionsAvailable || !actionRule || !outcome) return;
    const resolution = resolveModerationDecisionButtonClick(actionRule);
    if (!resolution.primaryAction) return;
    const primary = resolution.primaryAction;
    const appliedActions = (resolution.appliedActions || []).map((action) => ({
      id: action.id,
      actionType: action.actionType,
      ruleName: action.ruleName,
      actionTarget: action.actionTarget,
      decisionOutcome: action.decisionOutcome || '',
      allowedDurations: action.allowedDurations || [],
      restrictionScope: action.restrictionScope || '',
    }));
    const findings = flaggedItems.map((item) => ({ ...item }));
    const record = {
      moderationDecisionId: decisionIdFromCaseId(itemFromState?.caseId),
      decisionOutcome: outcome,
      decisionActionType: primary.actionType,
      decisionActionLabel: primary.ruleName || getActionTypeLabel(primary.actionType),
      decisionActionRuleId: primary.id,
      appliedActions,
      appliedActionsLabel: formatAppliedActions(appliedActions),
      findings,
      flaggedItems: findings,
      checks,
      decisionReason: decisionReason.trim(),
      fraudSecurityCaseId: null,
      finalisedBy: assignedHandler,
      finalisedOn: new Date().toISOString(),
    };
    setSaved(record);
    onFinalSave?.(record);
  };

  const executeTransfer = (actionRule, { force = false } = {}) => {
    if ((!force && !actionsAvailable) || !actionRule) return;
    if (force && (!actionsAvailable || !actionRule)) return;
    const resolution = resolveModerationDecisionButtonClick(actionRule);
    if (!resolution.primaryAction) return;
    const primary = resolution.primaryAction;
    const appliedActions = (resolution.appliedActions || []).map((action) => ({
      id: action.id,
      actionType: action.actionType,
      ruleName: action.ruleName,
      actionTarget: action.actionTarget,
      decisionOutcome: action.decisionOutcome || '',
    }));
    const record = {
      moderationDecisionId: null,
      decisionOutcome: 'Transfer',
      decisionActionType: primary.actionType,
      decisionActionLabel: primary.ruleName || getActionTypeLabel(primary.actionType),
      decisionActionRuleId: primary.id,
      appliedActions,
      appliedActionsLabel: formatAppliedActions(appliedActions),
      findings: flaggedItems,
      flaggedItems,
      checks,
      decisionReason: decisionReason.trim(),
      fraudSecurityCaseId: null,
      finalisedBy: assignedHandler,
      finalisedOn: new Date().toISOString(),
    };
    setSaved(record);
    onFinalSave?.(record);
  };

  const handleTransferClick = () => {
    if (!actionsAvailable || !TRANSFER_BUTTON) return;
    const normalized = normalizeCaseActionForm(TRANSFER_BUTTON);
    if (requiresConfirmationMessage(normalized.confirmationRequirement)) {
      setPendingConfirmRule(normalized);
      return;
    }
    executeTransfer(TRANSFER_BUTTON);
  };

  const handleRecordDecision = () => {
    if (!formReady || !derivedOutcome) return;
    const button = derivedOutcome === 'Confirmed' ? CONFIRMED_BUTTON : NOT_CONFIRMED_BUTTON;
    if (!button) return;
    executeFinal(button, derivedOutcome);
  };

  const confirmMessage =
    String(pendingConfirmRule?.confirmationMessage || '').trim() ||
    'Are you sure you want to transfer this case to Fraud & Security?';

  if (isNew && !isAssigned) {
    return (
      <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
        <div className="space-y-4">
          <AdminSupportingText className="text-offblack">
            Opening a case is view only. Press Assign to Me to take this case and move it to Under
            Review.
          </AdminSupportingText>
          <div className="flex flex-wrap justify-start gap-3">
            <AdminHeaderAction type="button" onClick={() => onAssignToMe?.()}>
              Assign to Me
            </AdminHeaderAction>
          </div>
        </div>
      </DashboardMainContainer>
    );
  }

  return (
    <div className="space-y-8">
      <CaseChecklist
        checks={checks}
        disabled={!decisionAvailable || locked}
        onToggle={toggleCheck}
      />

      {locked && saved ? (
        <RecordedDecisionFields record={saved} />
      ) : (
        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
          <div className="space-y-4">
            {flaggedItems.length ? (
              <div className="space-y-3">
                <AdminPanelTitle className="!mb-0">Flagged Items</AdminPanelTitle>
                {flaggedItems.map((item, index) => (
                  <div key={item.id} className="space-y-1.5">
                    <AdminDetailFieldLabel>
                      Item {index + 1}
                    </AdminDetailFieldLabel>
                    <AdminDetailFieldValue>
                      {item.label || item.preview || item.excerpt || '—'}
                    </AdminDetailFieldValue>
                    <div className="relative w-full min-w-0 max-w-sm">
                      <select
                        className={adminFormDetailSelectClasses(locked || !decisionAvailable)}
                        value={item.finding || ''}
                        disabled={!decisionAvailable || locked}
                        onChange={(e) => setItemFinding(item.id, e.target.value)}
                      >
                        <option value="">Select finding</option>
                        {FINDING_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      <AdminFormDropdownChevron disabled={locked || !decisionAvailable} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <AdminSupportingText>No identified flagged items.</AdminSupportingText>
            )}

            {derivedOutcome ? (
              <DetailField label="Decision Outcome" value={derivedOutcome} />
            ) : (
              <AdminSupportingText>
                Unable to Determine or missing findings mean there is no final outcome yet.
              </AdminSupportingText>
            )}

            <div>
              <AdminDetailFieldLabel className="block mb-1.5">
                Decision Reason
              </AdminDetailFieldLabel>
              <textarea
                value={decisionReason}
                onChange={(e) => setDecisionReason(e.target.value)}
                disabled={!decisionAvailable || locked}
                rows={3}
                className={`${adminFormTextareaClasses()} w-full min-h-[100px]`}
              />
            </div>

            <div className="flex flex-wrap justify-start gap-3">
              <AdminFormSecondaryAction
                type="button"
                disabled={!decisionAvailable || locked}
                onClick={() => onProgressSave?.(buildDraft())}
              >
                Save progress
              </AdminFormSecondaryAction>
              <AdminHeaderAction
                type="button"
                disabled={!actionsAvailable || !formReady}
                onClick={handleRecordDecision}
              >
                {derivedOutcome || 'Record decision'}
              </AdminHeaderAction>
              {TRANSFER_BUTTON ? (
                <AdminHeaderAction
                  type="button"
                  disabled={!actionsAvailable}
                  onClick={handleTransferClick}
                >
                  Transfer
                </AdminHeaderAction>
              ) : null}
            </div>
          </div>
        </DashboardMainContainer>
      )}

      {pendingConfirmRule && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="dashboard-panel font-inter fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] px-4"
              onClick={() => setPendingConfirmRule(null)}
              role="presentation"
            >
              <div
                className={`bg-white rounded-xl p-6 ${ACTION_POPUP_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray`}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="moderation-transfer-confirm-title"
              >
                <AdminPanelTitle
                  id="moderation-transfer-confirm-title"
                  className="text-offblack text-center"
                >
                  Are you sure?
                </AdminPanelTitle>
                <AdminSupportingText className="text-offblack text-center mt-3">
                  {confirmMessage}
                </AdminSupportingText>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <AdminFormSecondaryAction
                    type="button"
                    size="lg"
                    onClick={() => setPendingConfirmRule(null)}
                  >
                    No
                  </AdminFormSecondaryAction>
                  <AdminHeaderAction
                    type="button"
                    size="lg"
                    onClick={() => {
                      const rule = pendingConfirmRule;
                      setPendingConfirmRule(null);
                      executeTransfer(rule, { force: true });
                    }}
                  >
                    Yes
                  </AdminHeaderAction>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

const ModerationDecisionTab = ({
  scenario,
  itemFromState,
  caseStatus = 'New',
  assignedHandler = 'Unassigned',
  onFinalSave,
  onProgressSave,
  onAssignToMe,
  initialDraft,
  archivedRecord,
}) => {
  if (archivedRecord) {
    return (
      <ArchivedDecisionRecord
        record={{
          ...archivedRecord,
          checks:
            archivedRecord.checks ||
            getModerationDecisionChecklist().map((item) => ({
              ...item,
              checked: true,
            })),
        }}
      />
    );
  }
  return (
    <QueueDecisionForm
      scenario={scenario}
      itemFromState={itemFromState}
      caseStatus={caseStatus}
      assignedHandler={assignedHandler}
      onFinalSave={onFinalSave}
      onProgressSave={onProgressSave}
      onAssignToMe={onAssignToMe}
      initialDraft={initialDraft}
    />
  );
};

export default ModerationDecisionTab;
